import { useState, useEffect } from 'react';
import { Webhook, Plus, Trash2 } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import CollapsibleSection from './CollapsibleSection';
import SaveToRepoButton from './SaveToRepoButton';
import { useClaudeConfig } from './useClaudeConfig';

const HOOK_EVENTS = [
  'PreToolUse',
  'PostToolUse',
  'SessionStart',
  'SessionEnd',
  'UserPromptSubmit',
  'Stop',
  'SubagentStart',
  'SubagentStop',
] as const;

type HookEvent = (typeof HOOK_EVENTS)[number];

interface HookEntry {
  matcher: string;
  hooks: { type: string; command: string; timeout?: number }[];
}

type HooksConfig = Partial<Record<HookEvent, HookEntry[]>>;

export default function HooksSection() {
  const config = useClaudeConfig('.claude/settings.json');
  const [hooks, setHooks] = useState<HooksConfig>({});
  const [selectedEvent, setSelectedEvent] = useState<HookEvent>('PreToolUse');
  const [fullSettings, setFullSettings] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (config.content) {
      try {
        const parsed = JSON.parse(config.content);
        setFullSettings(parsed);
        setHooks(parsed.hooks || {});
      } catch {
        setFullSettings({});
        setHooks({});
      }
    }
  }, [config.content]);

  function persist(updated: HooksConfig) {
    setHooks(updated);
    const newSettings = { ...fullSettings, hooks: updated };
    setFullSettings(newSettings);
    config.setContent(JSON.stringify(newSettings, null, 2));
  }

  function addEntry() {
    const entries = hooks[selectedEvent] || [];
    persist({
      ...hooks,
      [selectedEvent]: [...entries, { matcher: '', hooks: [{ type: 'command', command: '' }] }],
    });
  }

  function removeEntry(index: number) {
    const entries = [...(hooks[selectedEvent] || [])];
    entries.splice(index, 1);
    const updated = { ...hooks, [selectedEvent]: entries };
    if (entries.length === 0) delete updated[selectedEvent];
    persist(updated);
  }

  function updateEntry(index: number, patch: Partial<HookEntry>) {
    const entries = [...(hooks[selectedEvent] || [])];
    entries[index] = { ...entries[index], ...patch };
    persist({ ...hooks, [selectedEvent]: entries });
  }

  function updateHookDetail(entryIdx: number, hookIdx: number, field: string, value: string | number) {
    const entries = [...(hooks[selectedEvent] || [])];
    const entry = { ...entries[entryIdx] };
    const hookList = [...entry.hooks];
    hookList[hookIdx] = { ...hookList[hookIdx], [field]: value };
    entry.hooks = hookList;
    entries[entryIdx] = entry;
    persist({ ...hooks, [selectedEvent]: entries });
  }

  const currentEntries = hooks[selectedEvent] || [];

  return (
    <CollapsibleSection
      title="Hooks"
      description="Automate validation and actions on tool use, session lifecycle, and more"
      filePath=".claude/settings.json → hooks"
      icon={Webhook}
      exists={Object.keys(hooks).length > 0}
      loading={config.loading}
    >
      {config.loading ? (
        <LoadingState size="md" />
      ) : (
        <div className="space-y-4">
          {/* Event selector */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Event:</label>
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value as HookEvent)}
              className="px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
            >
              {HOOK_EVENTS.map((ev) => (
                <option key={ev} value={ev}>
                  {ev} {hooks[ev]?.length ? `(${hooks[ev]!.length})` : ''}
                </option>
              ))}
            </select>
            <button
              onClick={addEntry}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            >
              <Plus className="w-3 h-3" /> Add hook
            </button>
          </div>

          {/* Hook entries for selected event */}
          {currentEntries.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">No hooks configured for {selectedEvent}.</p>
          ) : (
            <div className="space-y-3">
              {currentEntries.map((entry, entryIdx) => (
                <div
                  key={entryIdx}
                  className="p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 w-14 shrink-0">Matcher:</label>
                    <input
                      type="text"
                      value={entry.matcher}
                      onChange={(e) => updateEntry(entryIdx, { matcher: e.target.value })}
                      placeholder="e.g. Bash, Edit, Write"
                      className="flex-1 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-mono text-slate-900 dark:text-slate-100"
                    />
                    <button onClick={() => removeEntry(entryIdx)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {entry.hooks.map((hook, hookIdx) => (
                    <div key={hookIdx} className="flex items-center gap-2 ml-14">
                      <select
                        value={hook.type}
                        onChange={(e) => updateHookDetail(entryIdx, hookIdx, 'type', e.target.value)}
                        className="w-24 px-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100"
                      >
                        <option value="command">command</option>
                        <option value="http">http</option>
                        <option value="prompt">prompt</option>
                      </select>
                      <input
                        type="text"
                        value={hook.command || ''}
                        onChange={(e) => updateHookDetail(entryIdx, hookIdx, 'command', e.target.value)}
                        placeholder={hook.type === 'http' ? 'https://...' : './scripts/validate.sh'}
                        className="flex-1 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-mono text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="number"
                        value={hook.timeout || ''}
                        onChange={(e) => updateHookDetail(entryIdx, hookIdx, 'timeout', parseInt(e.target.value) || 0)}
                        placeholder="timeout"
                        className="w-16 px-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <SaveToRepoButton saving={config.saving} saved={config.saved} error={config.error} onSave={() => config.save()} />
        </div>
      )}
    </CollapsibleSection>
  );
}
